<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import ConnectMock from '@/components/landing/ConnectMock.vue';

// Sample school day, 8:00 AM to 4:00 PM, in minutes since midnight.
const DAY_START = 480;
const DAY_END = 960;
// Simulated minutes per real second once the dashboard is showing.
const SIM_SPEED = 16;
const TAB_DWELL = 900;

const oldTabs = [
  { title: 'Sign in', page: 'login', connect: true, url: 'myschoolapp.com/app#login' },
  { title: 'Assignment Center', page: 'assignments', connect: true, url: 'myschoolapp.com/app/student#studentmyday/assignment-center' },
  { title: 'Schedule', page: 'schedule', connect: true, url: 'myschoolapp.com/app/student#studentmyday/schedule' },
  { title: 'Biology', page: 'class', connect: true, url: 'myschoolapp.com/app/student#academicclass/bulletinboard' },
  { title: 'Calendar', page: 'calendar', connect: true, url: 'myschoolapp.com/app/student#calendar' },
  { title: 'Dining Menu', page: 'dining', connect: false, url: 'school-dining.com/menus/this-week.pdf' },
];

const dayBlocks = [
  { id: 1, start: 480, end: 540, name: 'Biology', room: 'Science 708' },
  { id: 2, start: 555, end: 615, name: 'French', room: 'Room 501' },
  { id: 3, start: 630, end: 690, name: 'English', room: 'Room 613' },
  { id: 4, start: 690, end: 750, name: 'Lunch', room: 'Dining hall', lunch: true },
  { id: 5, start: 750, end: 810, name: 'Geometry', room: 'Room 606' },
  { id: 6, start: 825, end: 885, name: 'Programming', room: 'Science 712' },
  { id: 7, start: 900, end: 960, name: 'Latin', room: 'Room 611' },
];

const dueSoon = [
  { title: 'Biology homework', due: 'Tomorrow' },
  { title: 'French essay', due: 'Thursday' },
  { title: 'Geometry quiz', due: 'Friday' },
];

const root = ref(null);
const phase = ref('tabs');
const played = ref(false);
const activeTab = ref(0);
const simMinutes = ref(520);
let timers = [];
let frame = 0;
let observer;

const formatMinutes = minutes => {
  const h24 = Math.floor(minutes / 60);
  const h12 = ((h24 + 11) % 12) + 1;
  return `${h12}:${String(Math.floor(minutes % 60)).padStart(2, '0')} ${h24 < 12 ? 'AM' : 'PM'}`;
};
const clock = computed(() => formatMinutes(Math.floor(simMinutes.value)));
const nowLineStyle = computed(() => {
  const pct = ((simMinutes.value - DAY_START) / (DAY_END - DAY_START)) * 100;
  // Fade at both ends of the day so the loop doesn't jump.
  const edge = Math.min(simMinutes.value - DAY_START, DAY_END - simMinutes.value);
  return { top: `${Math.max(0, Math.min(100, pct))}%`, opacity: Math.max(0, Math.min(1, edge / 12)) };
});
const blockStyle = block => {
  const span = DAY_END - DAY_START;
  return {
    top: `${((block.start - DAY_START) / span) * 100}%`,
    height: `calc(${((block.end - block.start) / span) * 100}% - 4px)`,
  };
};
// Tabs close right-to-left, each one slightly after the last, sliding toward the new tab.
const tabStyle = index => (phase.value === 'tabs' ? null : { transitionDelay: `${(oldTabs.length - 1 - index) * 110}ms` });

const stop = () => {
  timers.forEach(clearTimeout);
  timers = [];
  cancelAnimationFrame(frame);
};

const start = () => {
  stop();
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
    phase.value = 'ready';
    simMinutes.value = 650;
    return;
  }
  phase.value = 'tabs';
  activeTab.value = 0;
  simMinutes.value = 520;

  const collapseAt = 600 + oldTabs.length * TAB_DWELL;
  const readyAt = collapseAt + oldTabs.length * 110 + 900;
  timers = oldTabs.slice(1).map((_, i) => setTimeout(() => { activeTab.value = i + 1; }, 600 + (i + 1) * TAB_DWELL));
  timers.push(
    setTimeout(() => { phase.value = 'collapsing'; }, collapseAt),
    setTimeout(() => { phase.value = 'ready'; played.value = true; }, readyAt),
    setTimeout(() => {
      let last = performance.now();
      const tick = now => {
        simMinutes.value += (Math.min(now - last, 100) / 1000) * SIM_SPEED;
        last = now;
        if (simMinutes.value >= DAY_END) simMinutes.value = DAY_START;
        frame = requestAnimationFrame(tick);
      };
      frame = requestAnimationFrame(tick);
    }, readyAt + 800),
  );
};

onMounted(() => {
  // Wait until it's on screen so nobody misses the tabs folding away.
  if ('IntersectionObserver' in window) {
    observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        observer.disconnect();
        start();
      }
    }, { threshold: 0.45 });
    observer.observe(root.value);
  } else start();
});

onBeforeUnmount(() => {
  observer?.disconnect();
  stop();
});
</script>

<template>
  <figure ref="root" class="tab-demo" :class="`phase-${phase}`">
    <figcaption class="demo-heading">
      <div class="demo-copy">
        <h3>Six tabs.<br />Folded into one.</h3>
        <p>Sign-in, assignments, schedule, class pages, calendar, and a dining PDF. BetterConnect puts it all on one page.</p>
      </div>
      <div class="demo-status">
        <div class="steps" aria-hidden="true">
          <span class="step step-before"><i></i>{{ oldTabs.length }} tabs</span>
          <span class="step-line"><span></span></span>
          <span class="step step-after"><i></i>1 tab</span>
        </div>
        <button class="replay" :class="{ visible: phase === 'ready' && played }" :tabindex="phase === 'ready' && played ? 0 : -1" aria-label="Replay animation" @click="start">
          <svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M2.5 8a5.5 5.5 0 1 0 1.6-3.9M2.5 2.5v2.6h2.6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" /></svg>
        </button>
      </div>
      <p class="sr-only" aria-live="polite">{{ phase === 'ready' ? 'Now showing one BetterConnect tab with the whole day.' : phase === 'collapsing' ? 'Closing all six tabs.' : '' }}</p>
    </figcaption>
    <div class="browser" role="img" :aria-label="`Six school tabs closing into one BetterConnect tab`">
      <div class="chrome" aria-hidden="true">
        <div class="lights"><span></span><span></span><span></span></div>
        <div class="tabs">
          <div
            v-for="(tab, i) in oldTabs"
            :key="tab.title"
            class="tab tab-old"
            :class="{ 'is-active': i === activeTab }"
            :style="tabStyle(i)"
          >
            <span class="favicon" :class="tab.connect ? 'favicon-connect' : 'favicon-site'">{{ tab.connect ? 'C' : '' }}</span><span class="tab-title">{{ tab.title }}</span>
          </div>
          <div class="tab tab-new"><span class="favicon favicon-bc"></span><span class="tab-title">BetterConnect</span></div>
          <span v-if="phase === 'tabs'" class="tab-count">{{ oldTabs.length }} tabs</span>
        </div>
      </div>
      <div class="address" aria-hidden="true">
        <span>{{ phase === 'tabs' ? oldTabs[activeTab].url : 'betterconnect.app' }}</span>
      </div>

      <div class="page" aria-hidden="true">
        <div class="old-page"><ConnectMock :key="activeTab" :page="oldTabs[activeTab].page" class="old-page-inner" /></div>

        <div class="dash">
          <div class="dash-head reveal" style="--d: 0"><b>Tuesday</b><span>{{ clock }}</span></div>
          <div class="dash-grid">
            <div class="timeline reveal" style="--d: 1">
              <div
                v-for="block in dayBlocks"
                :key="block.id"
                class="block"
                :class="{ 'is-now': simMinutes >= block.start && simMinutes < block.end, 'is-past': simMinutes >= block.end, 'is-lunch': block.lunch }"
                :style="blockStyle(block)"
              >
                <span class="block-time">{{ formatMinutes(block.start) }}</span>
                <span class="block-name">{{ block.name }}</span>
                <span class="block-room">{{ block.room }}</span>
              </div>
              <div class="now-line" :style="nowLineStyle"><span></span></div>
            </div>
            <div class="side">
              <div class="panel reveal" style="--d: 2">
                <p class="panel-title">Due soon</p>
                <div v-for="(item, i) in dueSoon" :key="item.title" class="due">
                  <span class="check" :class="{ done: i === 0 && simMinutes >= 905 }"><svg viewBox="0 0 16 16" fill="none"><path d="m3.5 8.5 3 3 6-7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" /></svg></span>
                  <span><b>{{ item.title }}</b><small>{{ item.due }}</small></span>
                </div>
              </div>
              <div class="panel lunch reveal" style="--d: 3">
                <p class="panel-title">Lunch</p>
                <b>Chicken parmesan</b>
                <small>Caesar salad, fresh fruit</small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

  </figure>
</template>

<style scoped>
.tab-demo {
  --accent: #6759ed;
  --ease-out: cubic-bezier(0.16, 1, 0.3, 1);
  --chrome: color-mix(in srgb, var(--edge) 55%, var(--base));
  margin: 0;
}

.browser {
  overflow: hidden;
  border: 1px solid var(--edge);
  border-radius: 18px;
  background: var(--card);
  box-shadow: 0 40px 80px -40px rgb(20 16 50 / 0.35);
}

.chrome {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  padding: 10px 12px 0;
  background: var(--chrome);
}
.lights {
  display: flex;
  flex-shrink: 0;
  gap: 6px;
  padding-bottom: 11px;
}
.lights span {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--edge);
}

.tabs {
  position: relative;
  display: flex;
  flex: 1;
  min-width: 0;
  overflow: hidden;
}
.tab {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  height: 32px;
  padding: 0 11px;
  border-radius: 8px 8px 0 0;
  font-size: 12px;
  white-space: nowrap;
  color: var(--muted);
}
.tab-title {
  overflow: hidden;
  text-overflow: ellipsis;
}
.tab-old {
  flex: 1 1 0;
  max-width: 144px;
  border-right: 1px solid var(--edge);
  transform-origin: left center;
  transition:
    max-width 0.65s var(--ease-out),
    padding 0.65s var(--ease-out),
    transform 0.5s var(--ease-out),
    opacity 0.35s ease,
    background-color 0.2s ease,
    color 0.2s ease;
}
.tab-old.is-active {
  background: var(--card);
  color: var(--ink);
  border-right-color: transparent;
}
.tab-new {
  max-width: 0;
  padding: 0;
  opacity: 0;
  background: var(--card);
  color: var(--ink);
  font-weight: 600;
  transform: translateY(6px);
  transition:
    max-width 0.8s var(--ease-out) 0.75s,
    padding 0.8s var(--ease-out) 0.75s,
    transform 0.6s var(--ease-out) 0.85s,
    opacity 0.4s ease 0.85s;
}
.tab-count {
  position: absolute;
  right: 0;
  bottom: 8px;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--card);
  color: var(--muted);
  font-size: 11px;
  font-variant-numeric: tabular-nums;
}
.favicon {
  display: grid;
  flex-shrink: 0;
  place-items: center;
  width: 12px;
  height: 12px;
  border-radius: 3px;
  font-size: 8px;
  font-weight: 800;
  line-height: 1;
}
.favicon-connect {
  background: #3d6fd8;
  color: #fff;
}
.favicon-site {
  background: #c9763c;
}
.favicon-bc {
  background: var(--accent);
}

.phase-collapsing .tab-old,
.phase-ready .tab-old {
  max-width: 0;
  padding: 0;
  opacity: 0;
  border-right-width: 0;
  transform: scale(0.85);
}
.phase-collapsing .tab-new,
.phase-ready .tab-new {
  max-width: 176px;
  padding: 0 14px;
  opacity: 1;
  transform: none;
}

.address {
  padding: 9px 12px;
  border-bottom: 1px solid var(--edge);
}
.address span {
  display: block;
  overflow: hidden;
  padding: 6px 13px;
  border-radius: 999px;
  background: var(--chrome);
  color: var(--muted);
  font-size: 12px;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.page {
  position: relative;
  min-height: 400px;
  padding: 20px;
}
.old-page {
  position: absolute;
  inset: 0;
  z-index: 1;
  overflow: hidden;
  container-type: size;
  background: #eee;
  transition: opacity 0.45s ease, transform 0.7s var(--ease-out), filter 0.45s ease;
}
.old-page-inner {
  animation: swap 0.35s ease both;
}
@keyframes swap {
  from { opacity: 0; }
}
.phase-collapsing .old-page,
.phase-ready .old-page {
  opacity: 0;
  transform: scale(0.97);
  filter: blur(3px);
  pointer-events: none;
}

.reveal {
  opacity: 0;
  transform: translateY(10px);
  transition: opacity 0.5s ease, transform 0.6s var(--ease-out);
  transition-delay: calc(var(--d) * 90ms);
}
.phase-ready .reveal {
  opacity: 1;
  transform: none;
}

.dash-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 16px;
}
.dash-head b {
  font-size: 22px;
  font-weight: 650;
  letter-spacing: -0.5px;
}
.dash-head span {
  color: var(--muted);
  font-size: 14px;
  font-variant-numeric: tabular-nums;
}
.dash-grid {
  display: grid;
  grid-template-columns: minmax(0, 7fr) minmax(0, 5fr);
  gap: 20px;
}

.timeline {
  position: relative;
  height: 344px;
}
.block {
  position: absolute;
  left: 60px;
  right: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  column-gap: 8px;
  align-items: center;
  padding: 0 12px;
  border-left: 3px solid var(--edge);
  border-radius: 8px;
  background: var(--chrome);
  transition: background-color 0.35s ease, border-color 0.35s ease, opacity 0.35s ease;
}
.block-time {
  position: absolute;
  top: 6px;
  left: -60px;
  color: var(--muted);
  font-size: 11px;
  font-variant-numeric: tabular-nums;
}
.block-name,
.block-room {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.block-name {
  font-size: 14px;
  font-weight: 600;
}
.block-room {
  color: var(--muted);
  font-size: 12px;
}
.block.is-lunch {
  border: 1px dashed var(--edge);
  border-left-width: 3px;
  background: transparent;
}
.block.is-past {
  opacity: 0.45;
}
.block.is-now {
  border-left-color: var(--accent);
  background: color-mix(in srgb, var(--accent) 14%, transparent);
}
.now-line {
  position: absolute;
  left: 52px;
  right: 0;
  height: 2px;
  margin-top: -1px;
  background: var(--accent);
  pointer-events: none;
  transition: opacity 0.2s linear;
}
.now-line span {
  position: absolute;
  top: -4px;
  left: -4px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--accent);
}
.phase-tabs .now-line,
.phase-collapsing .now-line {
  visibility: hidden;
}

.side {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.panel {
  padding: 14px;
  border: 1px solid var(--edge);
  border-radius: 12px;
}
.panel-title {
  margin-bottom: 10px;
  color: var(--muted);
  font-size: 12px;
  font-weight: 600;
}
.panel b,
.panel small {
  display: block;
}
.panel b {
  font-size: 13px;
  font-weight: 600;
}
.panel small {
  color: var(--muted);
  font-size: 12px;
}
.due {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 6px 0;
}
.check {
  display: grid;
  flex-shrink: 0;
  place-items: center;
  width: 16px;
  height: 16px;
  margin-top: 1px;
  border: 1.5px solid var(--edge);
  border-radius: 50%;
  color: transparent;
  transition: background-color 0.25s ease, border-color 0.25s ease, color 0.25s ease;
}
.check svg {
  width: 10px;
  height: 10px;
}
.check.done {
  border-color: var(--accent);
  background: var(--accent);
  color: #fff;
}
.lunch b {
  font-size: 14px;
}

.demo-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 40px;
  margin-bottom: 28px;
}
.demo-copy h3 {
  margin-bottom: 18px;
  font-size: 29px;
  font-weight: 650;
  line-height: 1.2;
  letter-spacing: -0.8px;
}
.demo-copy p {
  max-width: 400px;
  color: var(--muted);
  font-size: 14px;
  line-height: 1.8;
}
.demo-status {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 8px;
}
.steps {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 14px;
  border: 1px solid var(--edge);
  border-radius: 999px;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}
.step {
  display: flex;
  align-items: center;
  gap: 7px;
  color: var(--muted);
  transition: color 0.4s ease, opacity 0.4s ease;
}
.step i {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
}
.step-before {
  color: var(--ink);
}
.step-before i {
  background: #3d6fd8;
}
.step-after {
  opacity: 0.55;
}
.phase-ready .step-before {
  color: var(--muted);
  opacity: 0.55;
}
.phase-ready .step-after {
  color: var(--ink);
  opacity: 1;
}
.phase-ready .step-after i {
  background: var(--accent);
}
.step-line {
  overflow: hidden;
  width: 36px;
  height: 2px;
  border-radius: 2px;
  background: var(--edge);
}
.step-line span {
  display: block;
  width: 100%;
  height: 100%;
  background: var(--accent);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 1.4s var(--ease-out);
}
.phase-collapsing .step-line span,
.phase-ready .step-line span {
  transform: none;
}
.replay {
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  color: var(--muted);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s ease, background-color 0.2s ease, color 0.2s ease;
}
.replay.visible {
  opacity: 1;
  pointer-events: auto;
}
.replay:hover {
  background: var(--edge);
  color: var(--ink);
}
.replay svg {
  width: 15px;
  height: 15px;
}
.sr-only {
  position: absolute;
  overflow: hidden;
  width: 1px;
  height: 1px;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

@media (max-width: 640px) {
  .demo-heading {
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
  }
  .page {
    min-height: 0;
    padding: 16px;
  }
  .dash-grid {
    grid-template-columns: 1fr;
  }
  .timeline {
    height: 300px;
  }
  .tab-old {
    max-width: 96px;
  }
  .tab-count {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .tab-demo *,
  .tab-demo *::before,
  .tab-demo *::after {
    animation-duration: 0.01ms !important;
    transition-duration: 0.01ms !important;
    transition-delay: 0s !important;
  }
}
</style>
